/**
 * Super-admin API client for test and demo accounts (PBI #1347).
 * Grant and revoke reuse the existing /admin/subscription endpoints.
 */

import apiClient from './apiClient';

/** Active subscription summary for the test and demo accounts screen. */
export interface AdminTestAccountSubscription {
  planId: number;
  planName: string;
  billingInterval: string;
  currentPeriodEnd: string;
  /** True when the subscription has no Stripe ids (an admin grant). */
  isAdminGrant: boolean;
}

/** Response from GET /admin/test-accounts/{userId}. */
export interface AdminTestAccountStatus {
  userId: string;
  isDemoAccount: boolean;
  /** True when the account is empty and may be flagged as a demo account. */
  canMarkDemo: boolean;
  /** Reasons marking is blocked; empty when it is allowed. */
  demoBlockers: string[];
  activeSubscription: AdminTestAccountSubscription | null;
}

export interface GrantPersonalRequest {
  planId: number;
  billingInterval: 'Monthly' | 'Annual';
  durationDays: number;
}

export interface SetDemoAccountRequest {
  isDemoAccount: boolean;
  /** Must be true when marking; the API rejects the call otherwise. */
  acknowledged: boolean;
  reason?: string;
}

/** Longest comp the admin screen offers (about ten years). */
export const MAX_GRANT_DURATION_DAYS = 3650;

export async function getAdminTestAccountStatus(userId: string): Promise<AdminTestAccountStatus> {
  const response = await apiClient.get<AdminTestAccountStatus>(
    `/admin/test-accounts/${encodeURIComponent(userId)}`,
  );
  return response.data;
}

export async function setAdminDemoAccount(
  userId: string,
  request: SetDemoAccountRequest,
): Promise<AdminTestAccountStatus> {
  const response = await apiClient.put<AdminTestAccountStatus>(
    `/admin/test-accounts/${encodeURIComponent(userId)}/demo`,
    request,
  );
  return response.data;
}

export async function grantAdminSubscription(
  userId: string,
  request: GrantPersonalRequest,
): Promise<void> {
  await apiClient.post(`/admin/subscription/users/${encodeURIComponent(userId)}/grant`, request);
}

export async function revokeAdminSubscription(userId: string): Promise<void> {
  await apiClient.post(`/admin/subscription/users/${encodeURIComponent(userId)}/revoke`);
}
